import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatINR, formatDate } from '../../utils/formatters';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Building2, Users, Percent, IndianRupee,
  Database, Activity, Clock, Shield, Server, Gauge,
} from 'lucide-react';

interface DashboardData {
  revenue: {
    mrr: number;
    arr: number;
    totalInvoiced: number;
    totalCollected: number;
    collectionRate: number;
    invoiceAging: { '0_30': number; '31_60': number; '61_90': number; '90_plus': number };
  };
  tenants: {
    total: number;
    active: number;
    totalUsers: number;
    activeUsers: number;
  };
  usage: {
    totalContacts: number;
    totalDeals: number;
    totalInvoices: number;
    totalProducts: number;
    totalTickets: number;
  };
  infrastructure: {
    uptime: string;
    dbSizeMB: number;
    apiResponseAvg: number;
    errorRate: number;
  };
  moduleAdoption: { module: string; totalRecords: number; tenantsUsing: number }[];
  recentAudit: {
    id: string;
    action: string;
    entity: string;
    entityId: string | null;
    tenantId: string | null;
    createdAt: string;
    user: { firstName: string; lastName: string; email: string } | null;
  }[];
}

function KPICard({ icon: Icon, label, value, subValue, color }: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  subValue?: string;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {subValue && <p className="text-xs text-gray-400">{subValue}</p>}
        </div>
      </div>
    </div>
  );
}

const AGING_COLORS: Record<string, string> = {
  '0_30': 'bg-green-500',
  '31_60': 'bg-yellow-500',
  '61_90': 'bg-orange-500',
  '90_plus': 'bg-red-500',
};

const AGING_LABELS: Record<string, string> = {
  '0_30': '0-30 days',
  '31_60': '31-60 days',
  '61_90': '61-90 days',
  '90_plus': '90+ days',
};

export function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(res => setData(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-gray-500">Failed to load dashboard data.</div>;
  }

  const agingTotal = Object.values(data.revenue.invoiceAging).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform Dashboard</h1>
        <p className="text-sm text-gray-500">AURA SaaS business overview &mdash; Ardhanex Tech</p>
      </div>

      {/* Quick Links */}
      <div
        onClick={() => navigate('/admin/resources')}
        className="flex cursor-pointer items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 p-4 transition hover:shadow-md"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-600">
          <Gauge size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">Resource Management</p>
          <p className="text-xs text-gray-500">Monitor tenant quotas, storage usage, and plan allocation</p>
        </div>
        <span className="text-xs font-medium text-violet-600">&rarr;</span>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          icon={IndianRupee}
          label="Monthly Recurring Revenue"
          value={formatINR(data.revenue.mrr)}
          subValue={`ARR: ${formatINR(data.revenue.arr)}`}
          color="bg-emerald-600"
        />
        <KPICard
          icon={Building2}
          label="Active Tenants"
          value={String(data.tenants.active)}
          subValue={`${data.tenants.total} total`}
          color="bg-blue-600"
        />
        <KPICard
          icon={Users}
          label="Total Users"
          value={String(data.tenants.activeUsers)}
          subValue={`${data.tenants.totalUsers} registered`}
          color="bg-violet-600"
        />
        <KPICard
          icon={Percent}
          label="Collection Rate"
          value={`${data.revenue.collectionRate}%`}
          subValue={`${formatINR(data.revenue.totalCollected)} collected`}
          color={data.revenue.collectionRate >= 50 ? 'bg-emerald-600' : 'bg-amber-600'}
        />
      </div>

      {/* Row 2: Revenue Details */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* ARR Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500">Annual Recurring Revenue</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900">{formatINR(data.revenue.arr)}</p>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
            <TrendingUp size={14} className="text-emerald-500" />
            Based on current MRR
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-medium text-gray-500">Revenue Breakdown</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Invoiced</span>
                <span className="font-medium text-gray-900">{formatINR(data.revenue.totalInvoiced)}</span>
              </div>
              <div className="mt-1 h-2 w-full rounded-full bg-gray-100">
                <div className="h-2 rounded-full bg-blue-500" style={{ width: '100%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Collected</span>
                <span className="font-medium text-gray-900">{formatINR(data.revenue.totalCollected)}</span>
              </div>
              <div className="mt-1 h-2 w-full rounded-full bg-gray-100">
                <div
                  className="h-2 rounded-full bg-emerald-500"
                  style={{ width: `${data.revenue.totalInvoiced > 0 ? (data.revenue.totalCollected / data.revenue.totalInvoiced) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Aging */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-medium text-gray-500">Invoice Aging</h3>
          {agingTotal === 0 ? (
            <p className="text-xs text-gray-400">No outstanding invoices</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(data.revenue.invoiceAging).map(([key, count]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${AGING_COLORS[key]}`} />
                  <span className="flex-1 text-xs text-gray-600">{AGING_LABELS[key]}</span>
                  <span className="text-xs font-semibold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Usage / Module Adoption */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Module Adoption */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-medium text-gray-500">Module Adoption</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 font-medium text-gray-500">Module</th>
                  <th className="pb-2 text-right font-medium text-gray-500">Tenants</th>
                  <th className="pb-2 text-right font-medium text-gray-500">Records</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.moduleAdoption.map(m => (
                  <tr key={m.module}>
                    <td className="py-2 font-medium capitalize text-gray-700">{m.module}</td>
                    <td className="py-2 text-right text-gray-600">{m.tenantsUsing}</td>
                    <td className="py-2 text-right font-semibold text-gray-900">{m.totalRecords.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-medium text-gray-500">Platform Usage Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Contacts', value: data.usage.totalContacts },
              { label: 'Deals', value: data.usage.totalDeals },
              { label: 'Invoices', value: data.usage.totalInvoices },
              { label: 'Products', value: data.usage.totalProducts },
              { label: 'Service Tickets', value: data.usage.totalTickets },
            ].map(item => (
              <div key={item.label} className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="text-lg font-bold text-gray-900">{item.value.toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 4: Infrastructure Health */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-medium text-gray-500">Infrastructure Health</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <Server size={16} className="mx-auto mb-1 text-emerald-500" />
            <p className="text-xs text-gray-500">Uptime</p>
            <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
              {data.infrastructure.uptime}
            </span>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <Database size={16} className="mx-auto mb-1 text-blue-500" />
            <p className="text-xs text-gray-500">DB Size</p>
            <p className="text-sm font-bold text-gray-900">{data.infrastructure.dbSizeMB} MB</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <Activity size={16} className="mx-auto mb-1 text-violet-500" />
            <p className="text-xs text-gray-500">API Latency</p>
            <p className="text-sm font-bold text-gray-900">
              {data.infrastructure.apiResponseAvg || '--'} ms
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <Shield size={16} className="mx-auto mb-1 text-emerald-500" />
            <p className="text-xs text-gray-500">Error Rate</p>
            <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
              {data.infrastructure.errorRate}%
            </span>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <Clock size={16} className="mx-auto mb-1 text-amber-500" />
            <p className="text-xs text-gray-500">Last Backup</p>
            <p className="text-xs font-medium text-gray-700">Configure</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <Shield size={16} className="mx-auto mb-1 text-blue-500" />
            <p className="text-xs text-gray-500">SSL Expiry</p>
            <p className="text-xs font-medium text-gray-700">Jun 2026</p>
          </div>
        </div>
      </div>

      {/* Row 5: Recent Activity */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-medium text-gray-500">Recent Activity (All Tenants)</h3>
        {data.recentAudit.length === 0 ? (
          <p className="text-xs text-gray-400">No audit logs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 font-medium text-gray-500">User</th>
                  <th className="pb-2 font-medium text-gray-500">Action</th>
                  <th className="pb-2 font-medium text-gray-500">Entity</th>
                  <th className="pb-2 font-medium text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.recentAudit.map(log => (
                  <tr key={log.id}>
                    <td className="py-1.5 text-gray-700">
                      {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System'}
                    </td>
                    <td className="py-1.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        log.action === 'create' ? 'bg-emerald-100 text-emerald-700' :
                        log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                        log.action === 'delete' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="py-1.5 capitalize text-gray-600">{log.entity}</td>
                    <td className="py-1.5 text-gray-400">{formatDate(log.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
